import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { FbqFn, MetaEventName, MetaEventParams } from '../model/analytics';
import { resolveProductKind } from '../model/product-kind';
import type { Store } from '../model/store';
import { ConsentCategory, consentCovers, expireCookiesByPrefix, reportConsentError } from '../consent/consent';
import { PRODUCTION_HOST } from '../consent/production-host';
import { VIPPS_CALLBACK_PATH } from './vipps-login.service';

export const META_PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

/** The most events track() holds while the consent banner's script has not run. */
const BEFORE_CONSENT_KNOWN_LIMIT = 10;

/**
 * The longest settled() lets a caller wait for Meta's script. Long enough for a
 * cached or CDN-fresh fetch, short enough that a slow network or a blocked
 * request never visibly holds a buyer.
 */
export const META_PIXEL_MAX_WAIT_MS = 1000;

/**
 * Cookie name prefixes the pixel writes on the registrable domain (`_fbp`, and
 * `_fbc` when the visitor arrived on an fbclid link). Expired on withdrawal.
 */
export const META_COOKIE_PREFIXES = ['_fbp', '_fbc'] as const;

/**
 * Routes the pixel never loads on and never sends from, whatever consent
 * says. The shop has no page a gift recipient lands on (recipients land on
 * minegavekort.app), but these are the buyer's post-purchase pages and they
 * show card details, so they are kept out of it on the same principle.
 * `vipps-callback` only redirects, but its address carries a Vipps login code.
 * Matched as the whole first path segment, never as a prefix: a partner slug
 * such as /distribution-as goes through the :storeslug route and is a shop.
 */
export const META_PIXEL_EXCLUDED_ROUTES = ['distribution', 'distributionlist', VIPPS_CALLBACK_PATH] as const;

/**
 * What a page tells the pixel about the store it shows. Partial because the
 * receipt reads the store back from localStorage, where it may be missing.
 */
export type PixelStore = Partial<Pick<Store, 'products' | 'isOwnCard'>>;

/**
 * Whether the pixel may run for a store: one of iGive's own cards (ticked in
 * cardAdmin) or a resold external card (the voucher kind, which is how Zalando
 * is sold). Partner stores never qualify: their buyers are the partner's
 * customers, not iGive's to send to its own ad accounts. Decided from the
 * store's data, never its name or slug, so a new own card or resold brand
 * needs no deploy. Anything unknown is no.
 */
export function storeQualifiesForPixel(store: PixelStore | null | undefined): boolean {
  const products = store?.products;
  return store?.isOwnCard === true || (!!products && resolveProductKind({ products }) === 'voucher');
}

/** The first path segment of an app URL, without query or fragment. */
export function firstPathSegment(url: string): string {
  return url.split(/[?#]/)[0].split('/')[1] ?? '';
}

/**
 * The Meta pixel, honouring the `markedsforing` consent category by loading
 * or not loading. Everything that can send to Meta goes through here.
 *
 * The pixel lives in Angular rather than in the gate in src/index.html for
 * three reasons: the pixel id comes from `environment`,
 * which the gate cannot read; whether to load depends on the route, which
 * needs the Router; and keeping it out of the gate keeps the gate's own
 * invariant literally true, that it never reads `markedsforing` and never
 * touches a Google ad_* signal.
 *
 * Every load and every event requires all of: a production build, a pixel id
 * configured, the exact production hostname, a marketing yes the hosted
 * consent banner says covers Meta, a buyer route, and a store that qualifies
 * (storeQualifiesForPixel).
 * Staging builds use the base environment, so the id is null there and
 * nothing here ever runs on staging or in CI.
 *
 * The store is whatever the page last declared through setStore(). Every
 * navigation forgets it, so a page that never declares one, or whose store
 * fails to load, sends nothing, and the PageView of a navigation waits until
 * its page has said which store it shows.
 *
 * What Meta is told: PageView on each buyer-route navigation, ViewContent,
 * InitiateCheckout and Purchase from AnalyticsService, with the card's slug as
 * the only product detail and, on the purchase, what iGive earns on a card-paid
 * order (the same figure Google Ads gets). Automatic event
 * detection is switched off before init and init is never given user data,
 * so nothing is matched or scraped from the page. Meta's own PageView on
 * history changes is switched off too, so the route rule above is the only
 * thing deciding when a PageView goes out.
 *
 * Meta's script stamps every event with the page URL as it sees it, and the
 * events queued before the script arrives get the URL of the moment it runs.
 * A page that queues an event and leaves at once would therefore report it
 * from wherever it went; settled() is how the receipt waits for the script
 * before moving on, so a Purchase carries the receipt and never a
 * post-purchase page.
 */
@Injectable({ providedIn: 'root' })
export class MetaPixelService implements OnDestroy {
  /** True once the script element has been injected into this document. */
  private loaded = false;
  /**
   * True between an in-document revoke() or hold() and the next grant. Meta's
   * script holds every event after `fbq('consent', 'revoke')` until told
   * `grant`, so a visitor who withdraws and then accepts again in the same
   * document, or who passes a partner's page on the way back to an own card,
   * would otherwise be measured by nothing until a reload.
   */
  private held = false;
  /** Resolves when the injected script has run or failed; null until injected. */
  private scriptSettled: Promise<void> | null = null;
  /** True once that promise has resolved, so a later settled() arms no timer. */
  private scriptDone = false;
  /** Whether the store the current page declared qualifies. False until one does. */
  private storeQualifies = false;
  /** True from a finished navigation until its PageView is sent. */
  private pageViewPending = false;
  /**
   * Events tracked before the consent banner's script has run, when nobody
   * yet knows the visitor's answer. Sent, or dropped, once it has; bounded
   * for a script that never loads.
   */
  private beforeConsentKnown: (() => void)[] = [];
  /** The navigation in flight, and both of the above as they were on the page on screen when it started. */
  private navigationInFlight: { id: number; storeQualifies: boolean; pageViewPending: boolean } | null = null;
  private navigation: Subscription;

  constructor(private router: Router) {
    // The returning-visitor path, the counterpart of the gate booting GTM
    // from a stored grant. Each buyer-route navigation is also a PageView,
    // which a single-page app has to report itself. Forgetting the store at
    // the start, not the end, leaves room for a page that declares its store
    // while it activates, before NavigationEnd. A navigation that never
    // lands leaves the same page on screen, so it gets its store back.
    // Matched by id: a skipped navigation never started, so it has nothing to
    // give back. A navigation that replaces one still in flight keeps the
    // first one's snapshot: that page is still on screen.
    this.navigation = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigationInFlight = {
          id: event.id,
          storeQualifies: this.navigationInFlight?.storeQualifies ?? this.storeQualifies,
          pageViewPending: this.navigationInFlight?.pageViewPending ?? this.pageViewPending,
        };
        this.storeQualifies = false;
        this.pageViewPending = false;
      } else if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        if (this.navigationInFlight?.id !== event.id) return;
        ({ storeQualifies: this.storeQualifies, pageViewPending: this.pageViewPending } = this.navigationInFlight);
        this.navigationInFlight = null;
      } else if (event instanceof NavigationEnd) {
        if (this.navigationInFlight?.id === event.id) this.navigationInFlight = null;
        this.pageViewPending = true;
        this.sendPendingPageView(event.urlAfterRedirects);
      }
    });
  }

  ngOnDestroy(): void {
    this.navigation.unsubscribe();
  }

  /**
   * Called through the consent gate on load and after every decision, beside
   * the gate's own apply(). Asks the banner rather than trusting the caller,
   * so a decision that failed to persist loads nothing, exactly like the gate.
   */
  applyConsent(): void {
    const url = this.router.url;
    // A re-grant after an in-document withdrawal lifts the hold now, not at
    // the next event. The page's PageView already went out before the
    // withdrawal, so it is not sent twice.
    if (this.held && this.mayRun(url)) this.ensureLoaded();
    this.sendPendingPageView(url);
    const waiting = this.beforeConsentKnown;
    this.beforeConsentKnown = [];
    waiting.forEach((send) => send());
  }

  /**
   * Called by a page once it knows which store it shows; pass null when it
   * failed to find out. A store that qualifies sends the page's PageView if it
   * is still waiting. One that does not puts a pixel already loaded in this
   * document on hold, so a visitor who came from an own-card page in the same
   * tab sends nothing from a partner's.
   */
  setStore(store: PixelStore | null): void {
    this.storeQualifies = storeQualifiesForPixel(store);
    if (this.storeQualifies) {
      this.sendPendingPageView(this.router.url);
    } else {
      this.hold();
    }
  }

  /**
   * Sends one event, or nothing. The params are passed through untouched: the
   * caller decides what Meta learns and the test on this class holds it to the
   * closed MetaEventParams shape. `eventId` is the payment id on a purchase, so
   * a later server-side copy can be de-duplicated against this one.
   */
  track(event: MetaEventName, params: MetaEventParams, eventId?: string): void {
    if (!window.igiveConsent) {
      if (this.beforeConsentKnown.length < BEFORE_CONSENT_KNOWN_LIMIT) {
        this.beforeConsentKnown.push(() => this.track(event, params, eventId));
      }
      return;
    }
    if (!this.mayRun(this.router.url)) return;
    this.ensureLoaded();
    const fbq = window.fbq;
    if (!fbq) return;
    if (eventId) {
      fbq('track', event, params, { eventID: eventId });
    } else {
      fbq('track', event, params);
    }
  }

  /**
   * Resolves once Meta's script has taken the events queued in this document,
   * or at once when nothing is waiting on it: no script was injected, it has
   * already run, or it failed to load. Never later than `maxWaitMs`, so a slow
   * CDN cannot hold the caller; an event still queued at that point is reported
   * from wherever the caller goes next, which is a worse URL and not a lost
   * event.
   */
  settled(maxWaitMs = META_PIXEL_MAX_WAIT_MS): Promise<void> {
    if (!this.scriptSettled || this.scriptDone) return Promise.resolve();
    return Promise.race([
      this.scriptSettled,
      new Promise<void>((resolve) => setTimeout(resolve, maxWaitMs)),
    ]);
  }

  /**
   * Withdrawal, mirroring the gate's revoke() contract: `scope` is the NEW
   * scope, a no-op while `markedsforing` survives, otherwise the pixel's
   * cookies are expired and the return value says whether a pixel is still
   * executing in this document. A loaded script cannot be unloaded, so only a
   * reload can finish the job; the caller offers one when this returns true.
   *
   * `fbq('consent', 'revoke')` stops the loaded pixel from sending anything
   * further and is called first, but it is not the mechanism: the mechanism is
   * that the next document never loads the script at all.
   */
  revoke(scope: readonly ConsentCategory[]): boolean {
    if (scope.includes('markedsforing')) return false;

    if (this.loaded) {
      try {
        window.fbq?.('consent', 'revoke');
        this.held = true;
      } catch (e) {
        reportConsentError(e, 'meta.revoke.signal');
      }
    }
    try {
      expireCookiesByPrefix(META_COOKIE_PREFIXES);
    } catch (e) {
      // A withdrawal that could not delete the identifiers must not go unseen.
      reportConsentError(e, 'meta.revoke.clearCookies');
    }
    return this.loaded;
  }

  private sendPendingPageView(url: string): void {
    if (!this.pageViewPending || !this.mayRun(url)) return;
    this.pageViewPending = false;
    this.ensureLoaded();
    window.fbq?.('track', 'PageView');
  }

  /**
   * Stops a loaded script from sending, with the same consent signal a
   * withdrawal uses; ensureLoaded() lifts it on the next event that may run.
   * Only a page with a store that does not qualify holds: holding on every
   * navigation would also catch a Purchase still queued when the receipt
   * moves on to the post-purchase page.
   */
  private hold(): void {
    if (!this.loaded || this.held) return;
    try {
      window.fbq?.('consent', 'revoke');
      this.held = true;
    } catch (e) {
      reportConsentError(e, 'meta.hold');
    }
  }

  private mayRun(url: string): boolean {
    return (
      environment.production &&
      !!environment.meta.pixelId &&
      typeof window !== 'undefined' &&
      window.location.hostname === PRODUCTION_HOST &&
      consentCovers('meta') &&
      this.storeQualifies &&
      !(META_PIXEL_EXCLUDED_ROUTES as readonly string[]).includes(firstPathSegment(url))
    );
  }

  /**
   * Meta's loader snippet, minus nothing: a queue that buffers calls until the
   * script arrives, then the script itself. Idempotent, and the flag is set
   * before the DOM insert so a throwing insert can never be retried into a
   * double injection.
   */
  private ensureLoaded(): void {
    if (this.loaded) {
      if (this.held) {
        // A re-grant after an in-document withdrawal: lift the hold the
        // revoke put on the still-loaded script. Nothing is re-injected.
        this.held = false;
        window.fbq?.('consent', 'grant');
      }
      return;
    }
    this.loaded = true;

    try {
      const pixelId = environment.meta.pixelId as string;
      const fbq = this.installQueue();
      // Read by the script as it starts, so it has to be on the stub before the
      // element goes in: without it Meta hooks pushState and sends a PageView
      // of its own on every in-app navigation, doubling the buyer routes and
      // reporting the excluded ones, which the route rule cannot prevent.
      fbq.disablePushState = true;
      // Off before init: no automatic button-click or microdata event detection.
      fbq('set', 'autoConfig', false, pixelId);
      // No second argument: no user data for advanced matching, ever.
      fbq('init', pixelId);

      const script = document.createElement('script');
      script.async = true;
      script.src = META_PIXEL_SCRIPT_URL;
      const settled = new Promise<void>((resolve) => {
        const done = () => {
          this.scriptDone = true;
          resolve();
        };
        script.addEventListener('load', done);
        script.addEventListener('error', done);
      });
      document.head.appendChild(script);
      // Only once the element is in: a throwing insert leaves nothing to wait for.
      this.scriptSettled = settled;
    } catch (e) {
      // Nothing further loads, and `loaded` stays true so nothing retries.
      // A consenting visitor never being measured is worth knowing about.
      reportConsentError(e, 'meta.load');
    }
  }

  private installQueue(): FbqFn {
    if (window.fbq) return window.fbq;
    const fbq: FbqFn = (...args: unknown[]) => {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue?.push(args);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    return fbq;
  }
}
